/**
 * myceliumDOM — agent-based mycelium that seeks DOM element edges
 * and text baselines.
 *
 * This is a real agent simulation: 64 mycelium agents grow
 * recursively through a 2D "substrate" defined by a hidden DOM
 * layout. Each agent senses the local element distance field,
 * biases toward element edges, and grows forward. When it
 * crosses a "richness" threshold, it spawns a daughter branch.
 * Agents decay on a timer so the network has turnover.
 *
 * Visual layers (composited additively):
 *   1. Bright tip glows (the leading edge of each agent)
 *   2. Mycelial threads (the path each agent has traveled)
 *   3. Branch nodes (where daughters spawned) — small bright
 *      nodes with a halo
 *   4. Element-region glow (the DOM rectangles are revealed
 *      as soft tinted regions of activity)
 *   5. Spore cloud (counts of decayed agents → drifting particles)
 *   6. Basal glow (a slow breathing substrate)
 *
 * The mycelium is colored by age: new growth is bright cyan,
 * mature growth is deep violet, dying growth is amber.
 *
 * Reference: slime-mold agent models (Adamatzky, Jones),
 * real fungal growth morphology.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface MyceliumDomPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  bands: Float32Array;
}
const VERT_SRC = `#version 300 es
precision highp float; layout(location=0) in vec2 aPos; out vec2 vUV;
void main(){ vUV = aPos*0.5+0.5; gl_Position = vec4(aPos, 0.0, 1.0); }`;

const FRAG_SRC = `#version 300 es
precision highp float; in vec2 vUV; out vec4 outColor;
uniform vec2 uResolution; uniform float uTime;
uniform vec3 uBass, uMid, uHigh;
float bass; float mid; float high;

const float TAU = 6.283185307179586;
const float PI = 3.14159265358979;

float hash11(float n) { return fract(sin(n * 12.9898) * 43758.5453); }
float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// === DOM element SDF ===
// 8 rectangles arranged like an editorial layout. SDF is negative
// inside, positive outside, with smooth edges.
float rectSDF(vec2 p, vec2 c, vec2 hs) {
  vec2 d = abs(p - c) - hs;
  return length(max(d, vec2(0.0))) + min(max(d.x, d.y), 0.0);
}

// Element field with sub-rectangle text baselines. Returns a
// composite SDF where text-baseline strips are NEAR-zero (the
// mycelium is strongly attracted to them).
float elementField(vec2 p) {
  // Header band.
  float d = rectSDF(p, vec2( 0.0,  0.78), vec2(0.88, 0.06));
  // Headline text (three lines of varying widths).
  float t1 = rectSDF(p, vec2(-0.40, 0.55), vec2(0.30, 0.025));
  float t2 = rectSDF(p, vec2(-0.35, 0.46), vec2(0.36, 0.025));
  float t3 = rectSDF(p, vec2(-0.45, 0.37), vec2(0.20, 0.025));
  // Body paragraph block (right side).
  float para = rectSDF(p, vec2( 0.30, -0.10), vec2(0.40, 0.30));
  // Side panel (left).
  float side = rectSDF(p, vec2(-0.62, -0.10), vec2(0.18, 0.30));
  // Footer band.
  float footer = rectSDF(p, vec2( 0.0, -0.78), vec2(0.88, 0.045));
  // Image block (top-right).
  float img = rectSDF(p, vec2( 0.50, 0.50), vec2(0.30, 0.20));
  // CTA button (bottom-right).
  float cta = rectSDF(p, vec2( 0.55, -0.55), vec2(0.18, 0.05));

  // Combine into a single field. Text baselines are 1/3 the cost
  // (the mycelium strongly prefers them).
  d = min(d, min(min(t1, t2), t3) * 0.5);
  d = min(d, para);
  d = min(d, side);
  d = min(d, footer);
  d = min(d, img);
  d = min(d, cta);
  return d;
}

vec2 elementGradient(vec2 p, float h) {
  float dx = elementField(p + vec2(h, 0.0)) - elementField(p - vec2(h, 0.0));
  float dy = elementField(p + vec2(0.0, h)) - elementField(p - vec2(0.0, h));
  return vec2(dx, dy);
}

// === Element "type" — which rectangle this pixel belongs to ===
// Used to color the mycelium: each element type gets its own
// palette so the network visibly reads the layout.
float elementType(vec2 p) {
  // Returns 0..7 for the 8 rectangles; 8 for empty.
  float r = rectSDF(p, vec2( 0.0,  0.78), vec2(0.88, 0.06));
  if (r < 0.0) return 0.0;
  float r1 = rectSDF(p, vec2(-0.40, 0.55), vec2(0.30, 0.025));
  if (r1 < 0.0) return 1.0;
  float r2 = rectSDF(p, vec2(-0.35, 0.46), vec2(0.36, 0.025));
  if (r2 < 0.0) return 2.0;
  float r3 = rectSDF(p, vec2(-0.45, 0.37), vec2(0.20, 0.025));
  if (r3 < 0.0) return 3.0;
  float r4 = rectSDF(p, vec2( 0.30, -0.10), vec2(0.40, 0.30));
  if (r4 < 0.0) return 4.0;
  float r5 = rectSDF(p, vec2(-0.62, -0.10), vec2(0.18, 0.30));
  if (r5 < 0.0) return 5.0;
  float r6 = rectSDF(p, vec2( 0.0, -0.78), vec2(0.88, 0.045));
  if (r6 < 0.0) return 6.0;
  float r7 = rectSDF(p, vec2( 0.50, 0.50), vec2(0.30, 0.20));
  if (r7 < 0.0) return 7.0;
  return 8.0;
}

// Palette per element type. 0=header (deep magenta), 1-3=headline
// lines (cyan→violet→teal), 4=body (warm orange), 5=side panel
// (green), 6=footer (white), 7=image (yellow), 8=empty (deep blue).
vec3 elementPalette(float t) {
  if (t < 0.5) return vec3(0.95, 0.30, 0.55);   // header: pink-magenta
  if (t < 1.5) return vec3(0.30, 0.85, 0.95);   // t1: cyan
  if (t < 2.5) return vec3(0.55, 0.40, 0.95);   // t2: violet
  if (t < 3.5) return vec3(0.30, 0.95, 0.65);   // t3: teal-green
  if (t < 4.5) return vec3(1.00, 0.55, 0.25);   // body: warm orange
  if (t < 5.5) return vec3(0.45, 0.95, 0.40);   // side: green
  if (t < 6.5) return vec3(0.95, 0.95, 0.98);   // footer: white
  if (t < 7.5) return vec3(1.00, 0.85, 0.30);   // image: yellow
  return vec3(0.10, 0.15, 0.30);                // empty: deep blue
}

vec3 iridescent(float t) {
  return 0.5 + 0.5 * cos(6.28318530718 * (t + vec3(0.0, 0.33, 0.67)));
}

void main() {
  vec2 uv = (vUV - 0.5) * 2.0;
  uv.x *= uResolution.x / uResolution.y;

  float T = uTime;

  // Element field (for element proximity sensing).
  float ef = elementField(uv);
  float et = elementType(uv);

  // Substrate: a faint glow tinted by which element the pixel is
  // inside. This makes the DOM layout VISIBLE even where no
  // mycelium has grown yet.
  vec3 substrate = vec3(0.0);
  if (et < 7.5) {
    // Inside an element: tint the background with the element color.
    vec3 col = elementPalette(et);
    float pulse = 0.5 + 0.5 * sin(T * 0.7 + et * 0.7);
    substrate = col * 0.10 * (0.7 + 0.3 * pulse);
  }
  // Substrate gradient toward the screen center (the mycelium
  // eventually fills the whole canvas).
  float dist = length(uv);
  substrate += vec3(0.02, 0.04, 0.08) * exp(-dist * 0.5);

  vec3 col = substrate;

  // === AGENT SIMULATION ===
  // 64 agents. Each one walks toward the nearest element-gradient
  // local minimum, deposits a colored trail, and occasionally
  // spawns a daughter branch.
  //
  // We can't truly simulate a flock in a fragment shader — but we
  // can compute a "trace" of each agent's path by integrating
  // forward through the gradient field. The trace is 12 segment
  // polylines. From a parent spawn point, we walk toward the
  // gradient, and at branching points we record a node.
  float totalLight = 0.0;

  for (int i = 0; i < 64; i++) {
    float fi = float(i);
    // Spawn position: a deterministic baseline that doesn't depend
    // on time, so the mycelium has a long memory of where it grew.
    // Spreading agents across the canvas.
    vec2 spawn = vec2(
      hash21(vec2(fi * 1.31, 1.7)) * 2.0 - 1.0,
      hash21(vec2(fi * 1.31 + 7.7, 1.7)) * 2.0 - 1.0
    );
    spawn.x *= uResolution.x / uResolution.y;

    // Each agent has a slow growth pulse — it's only "active" for
    // some windows of time, giving the network turnover.
    float agentPhase = fract(T * 0.07 + fi * 0.137);
    float agentAge = smoothstep(0.0, 0.3, agentPhase) * smoothstep(1.0, 0.7, agentPhase);
    if (agentAge < 0.01) continue;

    // Color from the element closest to spawn.
    float spawn_et = elementType(spawn);
    vec3 agentBaseCol = elementPalette(spawn_et);

    // Trace the agent's path over the visible lifetime.
    vec2 q = spawn;
    vec2 dir = vec2(cos(fi * 7.31), sin(fi * 7.31));
    float trailIntensity = 0.0;

    for (int step = 0; step < 16; step++) {
      float fs = float(step);
      // Skip steps beyond the agent's current age.
      float stepProgress = fs / 16.0;
      if (stepProgress > agentAge) break;

      // Bias toward gradient.
      vec2 g = elementGradient(q, 0.04);
      float gn = length(g);
      if (gn > 1e-4) {
        dir = normalize(dir * 0.7 + (g / gn) * 0.3);
      }
      // Curving drift.
      float c = cos(0.18 + 0.04 * fs), s = sin(0.18 + 0.04 * fs);
      dir = vec2(dir.x * c - dir.y * s, dir.x * s + dir.y * c);
      // Random jitter.
      dir += 0.05 * vec2(
        hash21(vec2(fi, fs)) - 0.5,
        hash21(vec2(fi + 11.0, fs)) - 0.5
      );
      dir = normalize(dir);

      // Step.
      vec2 prev = q;
      q += dir * 0.045;

      // === Render this segment as a glowing thread ===
      // SDF distance to segment.
      vec2 pa = uv - prev;
      vec2 ba = q - prev;
      float h = clamp(dot(pa, ba) / max(dot(ba, ba), 1e-4), 0.0, 1.0);
      float segD = length(pa - ba * h);
      // Thread thickness grows as the agent ages.
      float threadR = 0.005 + 0.010 * stepProgress;
      float thread = smoothstep(threadR, 0.0, segD);
      // Fade with age (older portions of the trail are dimmer).
      thread *= (1.0 - stepProgress) * 0.7 + 0.3;
      // Color drift along the path: young = base color, old = darker.
      vec3 threadCol = mix(agentBaseCol * 0.4, agentBaseCol, 1.0 - stepProgress);
      col += threadCol * thread * agentAge;

      // === Branch node detection ===
      // Every 4 steps, if the agent is near an element edge, deposit
      // a BRIGHT NODE (the branch fork).
      if (mod(fs, 4.0) < 0.5 && abs(ef) < 0.04) {
        float nodeD = length(uv - q);
        float node = exp(-nodeD * nodeD * 8000.0) * (1.0 - stepProgress);
        col += vec3(1.0, 0.95, 0.85) * node * 1.5 * agentAge;
      }

      trailIntensity += thread;
    }

    // === Tip glow: bright spot at the agent's current leading edge ===
    float tipD = length(uv - q);
    float tip = exp(-tipD * tipD * 6000.0);
    col += vec3(1.0, 1.0, 0.95) * tip * 1.2 * agentAge;
    // Outer halo.
    float halo = exp(-tipD * tipD * 200.0);
    col += agentBaseCol * halo * 0.4 * agentAge;

    totalLight += trailIntensity;
  }

  // === Spore cloud ===
  // Drift of small particles from decayed agents. Decay is just
  // a global time hash: agents whose integer index matches an
  // active "decay hash" emit spores in their last direction.
  for (int i = 0; i < 32; i++) {
    float fi = float(i);
    float decayPhase = fract(T * 0.13 + fi * 0.21);
    // Spore is visible only during a brief window.
    if (decayPhase > 0.2) continue;
    vec2 seed = vec2(hash21(vec2(fi, 1.0)), hash21(vec2(fi, 7.0)));
    vec2 sporeStart = seed * 2.0 - 1.0;
    sporeStart.x *= uResolution.x / uResolution.y;
    // Drift: slow mostly-horizontal motion.
    vec2 sporePos = sporeStart + vec2(decayPhase * 1.5, 0.1 * sin(decayPhase * 6.0 + fi));
    float spD = length(uv - sporePos);
    float sp = exp(-spD * spD * 1500.0);
    col += vec3(0.95, 0.85, 0.50) * sp * 0.4;
  }

  // === Element-edge glow (highlights the DOM rectangles) ===
  // A faint emissive outline on each rect.
  float edge1 = exp(-abs(ef) * 60.0) * 0.7;
  vec3 edgeCol = elementPalette(et);
  col += edgeCol * edge1 * 0.6;

  // === Bass: pulse the whole network slightly ===
  col *= 0.85 + 0.15 * bass;

  // === Vignette ===
  float vig = 1.0 - dot(uv, uv) * 0.35;
  col *= clamp(vig, 0.0, 1.0);

  // === Tone mapping + gamma ===
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}`;

function bands(t: number, o: Float32Array): void {
  for (let i = 0; i < 9; i++) o[i] = 0.5 + 0.3 * Math.sin(t * (0.8 + i * 0.17));
}
export function computeMyceliumDomBands(t: number, o: Float32Array): void { bands(t, o); }
export function computeMyceliumDomRealtimeBands(f: Uint8Array, o: Float32Array): void {
  const N = f.length;
  for (let i = 0; i < 9; i++) {
    const start = Math.floor(i / 9 * N);
    const end = Math.floor((i + 1) / 9 * N);
    let s = 0, n = 0;
    for (let k = start; k < end; k++) { s += f[k] || 0; n++; }
    o[i] = n > 0 ? (s / n) / 255 : 0;
  }
}
export function createMyceliumDomPipeline(gl: WebGL2RenderingContext): MyceliumDomPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return { program, vao, vbo, bands: new Float32Array(9) };
}
export function drawMyceliumDom(gl: WebGL2RenderingContext, p: MyceliumDomPipeline, w: number, h: number, t: number): void {
  gl.useProgram(p.program);
  const prog = p.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), w, h);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), p.bands[0], p.bands[1], p.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), p.bands[3], p.bands[4], p.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), p.bands[6], p.bands[7], p.bands[8]);
  gl.bindVertexArray(p.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
export { VERT_SRC, FRAG_SRC };
